import { Box, Typography, Button, Container, Grid, Card, CardContent, Fade, Chip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { keyframes } from '@mui/system'
import {
  MusicNoteRounded as MusicIcon,
  SettingsRounded as ControlIcon,
  EventRounded as ScheduleIcon,
  DashboardRounded as DashboardIcon,
  PlayArrowRounded as TrialIcon
} from '@mui/icons-material'
import PricingModal from '../components/PricingModal'
import { LoginModal } from '../components/LoginModal'
import { RegisterModal } from '../components/RegisterModal'
import SuccessModal from '../components/SuccessModal'
import AboutModal from '../components/AboutModal'
import { getAuth } from 'firebase/auth'
import { useUserSubscription } from '../hooks/useUserSubscription'

// Animations inspired by AN
const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`

const Home = () => {
  const [loaded, setLoaded] = useState(false)
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [registerModalOpen, setRegisterModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [aboutModalOpen, setAboutModalOpen] = useState(false)
  const [successModalData, setSuccessModalData] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' })
  const [user, setUser] = useState<any>(null)
  const navigate = useNavigate()
  const auth = getAuth()

  // Use our custom subscription hook
  const { isProActive, isTrialActive, loading: subscriptionLoading } = useUserSubscription(user)

  useEffect(() => {
    setLoaded(true)

    // Listen to auth state changes
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser)
    })

    // Handle Stripe checkout success/cancel
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const cancelled = urlParams.get('cancelled')

    if (success === 'true') {
      // Show success modal
      setSuccessModalData({
        type: 'success',
        title: 'Payment Successful! 🎉',
        message: 'Your subscription is now active! You can now manage your hotels and zones from the dashboard.'
      })
      setSuccessModalOpen(true)
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname)

      // Refresh user data after payment success (with delay to ensure webhook processing)
      setTimeout(() => {
        if (auth.currentUser) {
          auth.currentUser.reload()
        }
      }, 2000)
    } else if (cancelled === 'true') {
      // Show cancelled modal
      setSuccessModalData({
        type: 'error',
        title: 'Payment Cancelled',
        message: 'Your payment was cancelled. You can try again anytime from the pricing section.'
      })
      setSuccessModalOpen(true)
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    return () => unsubscribe()
  }, [auth])

  return (
    <>
      {/* Ultra Modern Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* Animated Background Elements */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(74, 144, 194, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: `${floatAnimation} 6s ease-in-out infinite`
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(46, 134, 171, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: `${floatAnimation} 8s ease-in-out infinite reverse`
        }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center" sx={{ minHeight: '100vh', py: 4 }}>
            
            {/* Left Side - Hero Content + Features */}
            <Grid item xs={12} lg={7}>
              <Fade in={loaded} timeout={800}>
                <Box sx={{ pr: { lg: 4 } }}>
                  {/* Badge */}
                  <Chip 
                    label="Professional Audio Management" 
                    sx={{
                      background: 'linear-gradient(135deg, rgba(74, 144, 194, 0.2), rgba(46, 134, 171, 0.2))',
                      color: '#ffffff',
                      border: '1px solid rgba(74, 144, 194, 0.3)',
                      mb: 3,
                      fontWeight: 600
                    }}
                  />

                  {/* Main Title */}
                  <Typography 
                    variant="h1" 
                    sx={{
                      color: '#ffffff',
                      fontWeight: 800,
                      mb: 3,
                      background: 'linear-gradient(135deg, #ffffff, rgba(74, 144, 194, 0.8))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: { xs: '2.5rem', md: '3rem', lg: '3.5rem' }
                    }}
                  >
                    Multi-Zone Audio Control for Hotels & Venues
                  </Typography>

                  {/* Subtitle */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 4,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      maxWidth: '500px'
                    }}
                  >
                    Professional audio management system for hotels, restaurants, and event venues. 
                    Control multiple zones, schedule events, and deliver exceptional audio experiences.
                  </Typography>

                  {/* Compact Features */}
                  <Box sx={{ mb: 4 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ControlIcon sx={{ color: '#4A90C2', mr: 2, fontSize: '2rem' }} />
                          <Box>
                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              Multi-Zone Control
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Independent zone management
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ScheduleIcon sx={{ color: '#2E86AB', mr: 2, fontSize: '2rem' }} />
                          <Box>
                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              Event Scheduling
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Smart automation system
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <MusicIcon sx={{ color: '#5BA3D4', mr: 2, fontSize: '2rem' }} />
                          <Box>
                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              Professional Audio
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              ASIO driver support
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Fade>
            </Grid>

            {/* Right Side - Product Card */}
            <Grid item xs={12} lg={5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Fade in={loaded} timeout={1200}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  width: '350px',
                  height: '570px',
                  p: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Logo */}
                    <Box sx={{ 
                      textAlign: 'center', 
                      mb: 3,
                      pt: 1
                    }}>
                      <Box 
                        component="img"
                        src="/rafilis-mp-logo.png"
                        alt="Rafilis MP"
                        sx={{
                          width: 80,
                          height: 80,
                          margin: '0 auto',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    {/* Features List */}
                    <Box sx={{ mb: 3, flex: 1 }}>
                      {[
                        'Multi-zone audio control',
                        'Professional ASIO drivers',
                        'Event scheduling system',
                        'Remote management',
                        'Hotel integration ready'
                      ].map((feature, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 1.5,
                          color: '#1e293b'
                        }}>
                          <Box sx={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            background: '#2048BC',
                            mr: 1.5,
                            flexShrink: 0
                          }} />
                          <Typography sx={{ 
                            fontSize: '0.85rem',
                            fontWeight: 500
                          }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    
                    {/* Pricing */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h3" sx={{ 
                        color: '#1e293b', 
                        fontWeight: 800,
                        mb: 0.5,
                        fontSize: '2.2rem'
                      }}>
                        $99
                        <Typography component="span" variant="h6" sx={{ 
                          color: 'rgba(30, 41, 59, 0.6)', 
                          fontWeight: 400,
                          fontSize: '1rem'
                        }}>
                          /month
                        </Typography>
                      </Typography>
                      <Typography sx={{ 
                        color: 'rgba(30, 41, 59, 0.7)',
                        fontSize: '0.8rem'
                      }}>
                        Perfect for professional venues
                      </Typography>
                    </Box>
                    
                    {/* Dynamic Action Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={
                        user && isProActive ? <DashboardIcon /> :
                        user && isTrialActive ? <DashboardIcon /> :
                        <TrialIcon />
                      }
                      sx={{
                        py: 1.8,
                        fontSize: '0.95rem',
                        mb: 1.5,
                        background: user && isProActive
                          ? 'linear-gradient(135deg, #10b981, #059669)' // Green for Pro users
                          : user && isTrialActive
                          ? 'linear-gradient(135deg, #f59e0b, #d97706)' // Orange for Trial users
                          : '#2048BC', // Blue for non-users
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: user && isProActive
                          ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                          : user && isTrialActive
                          ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                          : '0 4px 12px rgba(32, 72, 188, 0.3)',
                        '&:hover': {
                          background: user && isProActive
                            ? 'linear-gradient(135deg, #059669, #047857)'
                            : user && isTrialActive
                            ? 'linear-gradient(135deg, #d97706, #b45309)'
                            : '#1a3d9e',
                          boxShadow: user && isProActive
                            ? '0 6px 16px rgba(16, 185, 129, 0.4)'
                            : user && isTrialActive
                            ? '0 6px 16px rgba(245, 158, 11, 0.4)'
                            : '0 6px 16px rgba(32, 72, 188, 0.4)'
                        },
                        '&:disabled': {
                          background: 'rgba(100, 116, 139, 0.3)',
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                      disabled={subscriptionLoading}
                      onClick={() => {
                        if (user && (isProActive || isTrialActive)) {
                          navigate('/dashboard')
                        } else if (user) {
                          setPricingModalOpen(true)
                        } else {
                          setLoginModalOpen(true)
                        }
                      }}
                    >
                      {subscriptionLoading ? 'Loading...' :
                       user && isProActive ? 'Go to Dashboard' :
                       user && isTrialActive ? 'Go to Dashboard' :
                       user ? 'Upgrade to Pro' :
                       'Start 7-Day Free Trial'}
                    </Button>
                    
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                      <Button
                        variant="outlined"
                        sx={{ 
                          py: 1.5,
                          px: 2,
                          fontSize: '0.85rem',
                          borderRadius: '8px',
                          borderColor: 'rgba(30, 41, 59, 0.2)',
                          color: '#1e293b',
                          fontWeight: 600,
                          flex: 1,
                          '&:hover': {
                            borderColor: '#2048BC',
                            background: 'rgba(32, 72, 188, 0.05)',
                            color: '#2048BC'
                          }
                        }}
                        onClick={() => window.location.href = '/mp/products'}
                      >
                        Download
                      </Button>
                      <Button
                        variant="text"
                        sx={{ 
                          py: 1.5,
                          px: 2,
                          fontSize: '0.85rem',
                          borderRadius: '8px',
                          color: 'rgba(30, 41, 59, 0.7)',
                          fontWeight: 600,
                          flex: 1,
                          '&:hover': {
                            background: 'rgba(30, 41, 59, 0.05)',
                            color: '#1e293b'
                          }
                        }}
                        onClick={() => setAboutModalOpen(true)}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #0a0f1a 0%, #1a2332 100%)', 
        py: 6,
        borderTop: '1px solid rgba(74, 144, 194, 0.1)'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box 
                  component="img"
                  src="/rafilis-mp-logo.png"
                  alt="Rafilis MP"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    mr: 2
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700 }}>
                    Rafilis
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Audio Solutions
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                Professional multi-zone audio management for hotels, restaurants, and venues worldwide.
              </Typography>
            </Grid>

            {/* Product Links */}
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Features
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Pricing
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Download
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Updates
                </Button>
              </Box>
            </Grid>

            {/* Support Links */}
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Documentation
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Help Center
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Contact
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Community
                </Button>
              </Box>
            </Grid>

            {/* Company */}
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  About
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Blog
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Careers
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start', color: 'rgba(255,255,255,0.7)' }}>
                  Press
                </Button>
              </Box>
            </Grid>

            {/* Newsletter */}
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Stay Updated
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                Get the latest updates and news
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                sx={{ 
                  background: '#2048BC',
                  fontSize: '0.8rem',
                  px: 2,
                  '&:hover': {
                    background: '#1a3d9e'
                  }
                }}
              >
                Subscribe
              </Button>
            </Grid>
          </Grid>

          {/* Bottom Bar */}
          <Box sx={{ 
            borderTop: '1px solid rgba(255,255,255,0.1)', 
            mt: 4, 
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              © 2025 Rafilis Audio Solutions. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Button size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Privacy
              </Button>
              <Button size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Terms
              </Button>
              <Button size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Cookies
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Modal */}
      <PricingModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
      />

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSwitchToRegister={() => {
          setLoginModalOpen(false)
          setRegisterModalOpen(true)
        }}
      />

      {/* Register Modal */}
      <RegisterModal
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        onSwitchToLogin={() => {
          setRegisterModalOpen(false)
          setLoginModalOpen(true)
        }}
      />

      {/* Success/Error Modal */}
      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        type={successModalData.type}
        title={successModalData.title}
        message={successModalData.message}
        onNavigateToDashboard={() => {
          setSuccessModalOpen(false)
          navigate('/dashboard')
        }}
      />

      {/* About Modal */}
      <AboutModal
        open={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
      />

    </>
  )
}

export default Home
